import { useState, useEffect, useMemo } from 'react';
import loadingVideo from '../assets/loading1.mp4';

/**
 * LoadingVideo - Full-screen loading video with orientation handling
 *
 * Used for transition screens between pages (lobby → table, table → lobby)
 * Handles portrait rotation on mobile devices.
 */

interface LoadingVideoProps {
  /** Optional random start time (0-4 seconds). If not provided, generates random. */
  randomStartTime?: number;
}

export function LoadingVideo({ randomStartTime }: LoadingVideoProps) {
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });

  // Generate random start time if not provided (memoized to prevent re-generation on re-render)
  const startTime = useMemo(() => {
    if (randomStartTime !== undefined) return randomStartTime;
    return Math.floor(Math.random() * 41) / 10; // 0 to 4 seconds in 0.1s intervals
  }, [randomStartTime]);

  useEffect(() => {
    const handleResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine if we need landscape rotation - ALWAYS rotate on portrait
  const isPortrait = viewport.height > viewport.width;
  const needsRotation = isPortrait;

  // Virtual dimensions after rotation
  const effectiveWidth = needsRotation ? viewport.height : viewport.width;
  const effectiveHeight = needsRotation ? viewport.width : viewport.height;

  if (needsRotation) {
    // Rotated layout for portrait devices
    return (
      <div className="fixed inset-0 bg-black overflow-hidden z-[9999]">
        <div
          style={{
            position: 'absolute',
            width: effectiveWidth,
            height: effectiveHeight,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-90deg)',
            transformOrigin: 'center center',
          }}
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
            onLoadedMetadata={(e) => {
              e.currentTarget.currentTime = startTime;
            }}
          >
            <source src={loadingVideo} type="video/mp4" />
          </video>
        </div>
      </div>
    );
  }

  // Normal layout - video fills viewport width, clips top/bottom
  return (
    <div className="fixed inset-0 bg-black overflow-hidden flex items-center justify-center z-[9999]">
      <video
        autoPlay
        muted
        loop
        playsInline
        className="w-full h-auto min-h-full object-cover"
        onLoadedMetadata={(e) => {
          e.currentTarget.currentTime = startTime;
        }}
      >
        <source src={loadingVideo} type="video/mp4" />
      </video>
    </div>
  );
}

export default LoadingVideo;
